import { sanitizeArticleBody, sanitizeEmailView, sanitizeRichText } from '@bms2/shared';

/**
 * 🔒 What the editing surface is allowed to be handed (T2956; decision 1240). A stored value reaches the surface's
 * `innerHTML` when a person opens it to edit, so it passes the consumer's allowed table first, as `RichTextView` passes
 * it before drawing. The tables live in `packages/shared`; this file only fits them to the surface.
 */

const IMAGE_TAG = /<img\b[^>]*>/gi;
const FILE_ID_ATTRIBUTE = /\bdata-(?:mail|kb)-file=/i;
const INLINE_SRC = /\bsrc="(data:image\/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+)"/i;
const ALT = /\balt="([^"<>]*)"/i;
/** The mark a held picture's number is wrapped in while the allowed table runs (T2980): a private-use character no stored text carries. */
const HELD_MARK = '\uE002';
const HELD = new RegExp(`${HELD_MARK}(\\d+)${HELD_MARK}`, 'g');

/**
 * 🔨 A picture the author inserted inline (the picture control with no upload to hand it to) is a bounded data address
 * with, at most, its words. It is lifted out before the allowed table runs and rebuilt from those two validated parts,
 * so the table does not strip a picture the surface drew itself and nothing else about the tag survives.
 */
export function keepingInlinePictures(clean: (html: string) => string): (html: string) => string {
  return (html) => {
    const kept: string[] = [];
    const held = html.split(HELD_MARK).join('').replace(IMAGE_TAG, (tag) => {
      if (FILE_ID_ATTRIBUTE.test(tag)) return tag;
      const src = INLINE_SRC.exec(tag)?.[1];
      if (!src) return tag;
      const alt = ALT.exec(tag)?.[1];
      kept.push(`<img src="${src}"${alt ? ` alt="${alt}"` : ''}>`);
      return `${HELD_MARK}${kept.length - 1}${HELD_MARK}`;
    });
    return clean(held).replace(HELD, (_whole, index: string) => kept[Number(index)] ?? '');
  };
}

/** The core editor's table. */
export const sanitizeCoreSurface = keepingInlinePictures(sanitizeRichText);

/** The email editor's table, which keeps the bytes a template's picture is drawn with. */
export const sanitizeEmailSurface = keepingInlinePictures(sanitizeEmailView);

const FILE_PICTURE = /<img\b([^>]*)>/gi;
const CANONICAL_PICTURE = /<img data-kb-file="([0-9a-f-]{36})"/g;
const KB_FILE = /\bdata-kb-file="([0-9a-fA-F-]{36})"/i;
const DRAWN_ADDRESS = /\bsrc="(data:image\/(?:png|jpeg|gif|webp);base64,[A-Za-z0-9+/=]+|blob:[^"\s<>]+|https?:\/\/[^"\s<>]+|\/[^"\s<>]*)"/i;

/**
 * The article editor's table: the article body through its own allowed table, then each picture given back the address
 * the surface drew it from, which is read from the value itself and kept only where it is a bounded data address, a
 * blob address the upload just made, an absolute web address or a path of the application's own.
 */
export function sanitizeArticleSurface(html: string): string {
  const drawn = new Map<string, string>();
  for (const match of html.matchAll(FILE_PICTURE)) {
    const attributes = match[1] ?? '';
    const id = KB_FILE.exec(attributes)?.[1]?.toLowerCase();
    const src = DRAWN_ADDRESS.exec(attributes)?.[1];
    if (id && src && !drawn.has(id)) drawn.set(id, src);
  }
  // A picture with no address stays in its stored form: the surface can hold it, and it is drawn once the upload gives it one.
  return sanitizeArticleBody(html).replace(CANONICAL_PICTURE, (whole, id: string) => {
    const address = drawn.get(id.toLowerCase());
    return address ? `<img src="${address.replace(/&/g, '&amp;')}" data-kb-file="${id}"` : whole;
  });
}
